// Zweite Bestätigung beim Massenstart eines Workflows (Agent 4C, Korrektur Runde 4). Ab `MASSEN_AB` Leads reicht ein Klick
// nicht: der Dialog nennt die Schritte in Klartext und verlangt, die Zahl der Leads einzutippen. So startet niemand aus
// Versehen 415 Läufe, deren Aufgaben, Zuweisungen und Statuswechsel in Close schwer rückgängig zu machen sind.
import type { Schritt, Workflow } from "../api/workflows";
import { Icon } from "../components/Icon";
import { dauerKurz, faelligKurz, schrittInfo } from "./hilfen";

/** Ab so vielen Leads wird zusätzlich bestätigt (der Server verlangt `bestaetigt` ab 10). */
export const MASSEN_AB = 10;

/** Schritte, die im Namen einer Person nach Close schreiben (Aufgaben, Bearbeiter, Status, Felder). */
const SCHREIBT_NACH_CLOSE = new Set(["aufgabe", "anrufaufgabe", "zuweisen", "lead_aendern"]);

const text = (v: unknown) => (typeof v === "string" ? v.trim() : "");

/** Ein Schritt in einem Satz, ohne Fachwörter. */
export function schrittSatz(s: Schritt): string {
  const c = s.config;
  switch (s.art) {
    case "aufgabe":
      return `Aufgabe „${text(c.text) || "ohne Text"}“, fällig ${faelligKurz(c)}`;
    case "anrufaufgabe":
      return `Anrufaufgabe „${text(c.text) || "Anrufen"}“, fällig ${faelligKurz(c)}`;
    case "email_entwurf":
      return "E-Mail als Entwurf zur Prüfung (wird nicht gesendet)";
    case "sms":
      return "SMS als Entwurf zur Prüfung (wird nicht gesendet)";
    case "zuweisen":
      return c.modus === "zufall" ? "Bearbeiter zufällig aus einer Gruppe setzen" : "Bearbeiter setzen";
    case "lead_aendern":
      return `${text(c.status_id) ? "Status" : ""}${text(c.status_id) && Object.keys((c.felder as object | undefined) ?? {}).length ? " und " : ""}${Object.keys((c.felder as object | undefined) ?? {}).length ? "Felder" : ""} am Lead ändern`;
    case "filter":
      return "Lauf beenden, wenn der Lead nicht passt";
    case "verzoegerung":
      return `Warten: ${dauerKurz(c)}`;
    default:
      return schrittInfo(s.art).label;
  }
}

export function zahlAusEingabe(eingabe: string): number | null {
  const ziffern = eingabe.replace(/[.\s]/g, "");
  return /^\d+$/.test(ziffern) ? Number(ziffern) : null;
}

export function MassenBestaetigung({
  workflow,
  anzahl,
  eingabe,
  onEingabe,
}: {
  workflow: Workflow;
  anzahl: number;
  eingabe: string;
  onEingabe: (wert: string) => void;
}) {
  const schreibt = workflow.schritte.some((s) => SCHREIBT_NACH_CLOSE.has(s.art));
  return (
    <div className="wf-massen" role="group" aria-label="Massenstart bestätigen">
      <div className="wf-massen__kopf">
        <Icon name="warnung" groesse={15} />
        <strong>
          {anzahl.toLocaleString("de-DE")} Läufe auf einmal starten
        </strong>
      </div>
      <p className="wf-massen__text">
        Für jeden dieser Leads läuft „{workflow.name}“ mit {workflow.schritte.length === 1 ? "diesem Schritt" : `diesen ${workflow.schritte.length} Schritten`}:
      </p>
      <ol className="wf-massen__schritte">
        {workflow.schritte.map((s) => (
          <li key={s.id}>
            {schrittSatz(s)}
            {SCHREIBT_NACH_CLOSE.has(s.art) ? <span className="wf-massen__close"> · schreibt nach Close</span> : null}
          </li>
        ))}
      </ol>
      {schreibt ? <p className="wf-massen__text">Änderungen in Close lassen sich nicht gesammelt zurücknehmen. Gesendet wird nichts.</p> : null}
      <label className="feld wf-massen__feld">
        <span className="feld__label">Zur Bestätigung die Zahl der Leads eintippen ({anzahl.toLocaleString("de-DE")})</span>
        <input
          className="eingabe wf-massen__zahl"
          inputMode="numeric"
          autoComplete="off"
          value={eingabe}
          onChange={(e) => onEingabe(e.target.value)}
          aria-invalid={eingabe !== "" && zahlAusEingabe(eingabe) !== anzahl}
        />
      </label>
    </div>
  );
}
